import { round, toInteger } from 'lodash-es';

export type WeightType = 'g' | 'kg' | 'lb';

const KG = 2.2046226218;
const fractionDigits = 3; // minimum 3
const weightConversion = {
  lb2kg: (value: number) => value / KG,
  lb2g: (value: number) => weightConversion.kg2g(weightConversion.lb2kg(value)),
  kg2lb: (value: number) => round(value * KG, fractionDigits - 1), // must be one less than fractionDigits otherwise causes issues with conversions
  kg2g: (value: number) => toInteger(value * 1000),
  g2kg: (value: number) => value / 1000,
  g2lb: (value: number) => weightConversion.kg2lb(weightConversion.g2kg(value)),
};

export function convertWeight(
  value: number,
  from: WeightType,
  to: WeightType,
  decimals?: number,
): number {
  if (from === to) {
    return value;
  }

  const conversionFn = (weightConversion as any)[`${from}2${to}`];
  if (conversionFn === undefined) {
    throw new Error(`Unknown weight conversion from '${from}' to '${to}'.`);
  }
  return roundWeight(conversionFn(value), decimals ?? fractionDigits);
}

export function roundWeight(value: number, decimals: number) {
  return round(value, decimals);
}

export function kilogramsToPounds(value: number, decimalPlaces?: number) {
  return convertWeight(value, 'kg', 'lb', decimalPlaces);
}

export function kilogramsToGrams(value: number, decimalPlaces?: number) {
  return convertWeight(value, 'kg', 'g', decimalPlaces);
}

export function gramsToKilograms(value: number, decimalPlaces?: number) {
  return convertWeight(value, 'g', 'kg', decimalPlaces);
}

export function gramsToPounds(value: number, decimalPlaces?: number) {
  return convertWeight(value, 'g', 'lb', decimalPlaces);
}

export function poundsToKilograms(value: number, decimalPlaces?: number) {
  return convertWeight(value, 'lb', 'kg', decimalPlaces);
}

export function poundsToGrams(value: number, decimalPlaces?: number) {
  return convertWeight(value, 'lb', 'g', decimalPlaces);
}
